<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, requiredIf, helpers } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store.js';
import useVuelidate from '@vuelidate/core';

import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import SearchResultConversationItem from './SearchResultConversationItem.vue';
import SelectConversationForm from './SelectConversationForm.vue';
import TestToolFormField from './TestToolFormField.vue';

const props = defineProps({
  parameterFields: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');

const formState = ref({});
const formRules = {
  conversation: {
    required,
  },
};

// Custom validator for array fields - checks that all items have a value
const requiredArray = isRequired =>
  helpers.withParams({ type: 'requiredArray' }, value => {
    if (!isRequired) return true;
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(item => item !== null && item !== '' && item !== undefined)
    );
  });

// Custom validator for number fields (integer/float)
const validNumber = fieldType =>
  helpers.withParams({ type: 'validNumber' }, value => {
    if (fieldType !== 'integer' && fieldType !== 'float') return true;
    if (value === null || value === '' || value === undefined) return true;
    return !Number.isNaN(Number(value));
  });

// Custom validator for array of numbers
const validNumberArray = fieldType =>
  helpers.withParams({ type: 'validNumberArray' }, value => {
    if (fieldType !== 'integer' && fieldType !== 'float') return true;
    if (!Array.isArray(value)) return true;
    return value.every(
      item =>
        item === null ||
        item === '' ||
        item === undefined ||
        !Number.isNaN(Number(item))
    );
  });

props.parameterFields.forEach(field => {
  const defaultValue = field.type === 'boolean' ? false : null;
  if (field.is_array) {
    formState.value[field.name] = field.required ? [defaultValue] : [];
  } else {
    formState.value[field.name] = defaultValue;
  }

  const isRequired = field.type !== 'boolean' && field.required;

  if (field.is_array) {
    formRules[field.name] = {
      requiredArray: requiredArray(isRequired),
      validNumberArray: validNumberArray(field.type),
    };
  } else {
    formRules[field.name] = {
      required: requiredIf(() => isRequired),
      validNumber: validNumber(field.type),
    };
  }
});

const initialState = {
  conversation: '',
};
props.parameterFields.forEach(field => {
  initialState[field.name] = field.is_array ? [] : null;
});

const castParameterValue = (value, type, isArray = false) => {
  if (isArray) {
    return value
      .map(v => castParameterValue(v, type))
      .filter(v => v !== null && v !== '');
  }

  if (type === 'integer' || type === 'float') {
    if (value === '' || value === null) return null;
    return type === 'integer' ? Math.round(value) : Number(value);
  }
  if (type === 'boolean') {
    return !!value;
  }
  return value === null || value === undefined ? null : `${value}`;
};

const state = computed(() => {
  const data = {
    conversation: formState.value.conversation,
  };
  props.parameterFields.forEach(field => {
    const value = formState.value[field.name];
    const type = field.type;
    data[field.name] = castParameterValue(value, type, field.is_array);
  });
  return data;
});

const v$ = useVuelidate(formRules, formState);

const validate = async () => {
  const isValid = await v$.value.$validate();
  return isValid;
};

const handleSelect = (conversation, toggle) => {
  toggle();
  formState.value.conversation = conversation;
};

const getFieldLabel = field => {
  return field.name + (field.required ? ' *' : '');
};

const addArrayItem = field => {
  const defaultValue = field.type === 'boolean' ? false : null;
  formState.value[field.name].push(defaultValue);
};

const removeArrayItem = (field, index) => {
  formState.value[field.name].splice(index, 1);
};

const updateArrayItem = (field, index, value) => {
  formState.value[field.name][index] = value;
};

defineExpose({ state, validate });
</script>

<template>
  <form class="space-y-4">
    <label
      class="mb-0.5 text-sm font-medium text-n-slate-12 flex flex-col gap-1 relative"
    >
      {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CONVERSATION.TITLE') }}
    </label>

    <Dropdown body-class="bg-n-solid-3 max-h-[calc(100vh-var(--top)-1rem)]">
      <template #trigger="{ toggle }">
        <Button
          size="sm"
          :color="v$.conversation.$error ? 'ruby' : 'slate'"
          :label="
            state.conversation
              ? t(
                  'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CONVERSATION.CHANGE_BUTTON_LABEL'
                )
              : t(
                  'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CONVERSATION.BUTTON_LABEL'
                )
          "
          variant="faded"
          :icon="state.conversation ? 'i-lucide-edit-2' : 'i-lucide-plus'"
          @click.prevent="toggle"
        />
      </template>
      <template #default="{ toggle }">
        <SelectConversationForm
          @close="toggle"
          @select="e => handleSelect(e, toggle)"
        />
      </template>
    </Dropdown>
    <SearchResultConversationItem
      v-if="state.conversation"
      :id="state.conversation.id"
      class="bg-n-slate-3 hover:bg-n-slate-3 dark:bg-n-solid-2 dark:hover:bg-n-solid-2 mt-2"
      :name="state.conversation.contact.name"
      :email="state.conversation.contact.email"
      :account-id="accountId"
      :inbox="state.conversation.inbox"
      :created-at="state.conversation.created_at"
      :email-subject="state.conversation.mail_subject"
    />
    <span
      v-if="v$.conversation.$error"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      {{
        t(
          'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.CONVERSATION.ERRORS.REQUIRED'
        )
      }}
    </span>

    <hr class="!border-n-weak !my-6" />

    <div class="space-y-4">
      <div
        v-for="field of parameterFields"
        :key="field.name"
        class="form-field"
      >
        <!-- Array Field -->
        <div v-if="field.is_array" class="space-y-2">
          <label
            class="mb-0.5 text-sm font-medium text-n-slate-12 flex gap-1 items-center relative"
          >
            <span>{{ getFieldLabel(field) }}</span>
            <div
              v-if="field.description"
              v-tooltip.right="{
                content: field.description,
                container: false,
              }"
              class="flex items-center justify-center flex-shrink-0 rounded-full hover:bg-n-alpha-2 size-4"
            >
              <Icon
                icon="i-lucide-info"
                class="flex-shrink-0 text-n-slate-11 size-3"
              />
            </div>
          </label>
          <div
            v-for="(item, index) in formState[field.name]"
            :key="index"
            class="flex gap-2 items-center"
          >
            <div class="flex-1">
              <TestToolFormField
                :model-value="item"
                :type="field.type"
                name=""
                description=""
                :required="false"
                :rules="{}"
                @update:model-value="v => updateArrayItem(field, index, v)"
              />
            </div>
            <Button
              size="xs"
              color="ruby"
              variant="ghost"
              icon="i-lucide-trash-2"
              class="mt-0.5"
              @click.prevent="removeArrayItem(field, index)"
            />
          </div>
          <Button
            size="sm"
            color="slate"
            variant="faded"
            icon="i-lucide-plus"
            :label="
              t(
                'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.PARAMETERS.ADD_ITEM_LABEL'
              )
            "
            @click.prevent="addArrayItem(field)"
          />
          <span
            v-if="v$[field.name]?.$error"
            class="block min-w-0 mt-1 mb-0 text-xs truncate text-n-ruby-9 dark:text-n-ruby-9"
          >
            {{
              t(
                'WGPT_BOTS.EDIT.TABS.TOOLS.TEST_TOOL.FORM.PARAMETERS.ERRORS.REQUIRED'
              )
            }}
          </span>
        </div>

        <!-- Single Field -->
        <TestToolFormField
          v-else
          v-model="formState[field.name]"
          :type="field.type"
          :name="field.name"
          :description="field.description"
          :required="field.required == true"
          :rules="formRules[field.name]"
        />
      </div>
    </div>
  </form>
</template>
